import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box, Button, Card, CardHeader, CardContent, Collapse, Typography,
    Grid, FormControl, FormGroup, FormLabel, OutlinedInput,
    Select, MenuItem, Divider
} from '@material-ui/core'
import Loader from 'components/general/Loader'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles(componentStyles)

function SmsFollowupsCard({ companyDetails, updateField, updateCompany }) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()

    const profileDetails = companyDetails.profileDetails || {}
    const slug = companyDetails.slug || ''
    const companyName = companyDetails.name || ''

    const smsFollowupsEnabled = profileDetails.smsFollowupsEnabled || false
    const smsCompanyName = profileDetails.smsCompanyName || ''
    const smsPhoneNumber = profileDetails.smsPhoneNumber || ''
    const reviewsRating = profileDetails.reviewsRating || ''
    const reviewsButtonLink = profileDetails.reviewsButtonLink || ''
    const reviewsPlatform = profileDetails.reviewsPlatform || ''

    const [loading, setLoading] = useState(false)

    const profileUrl = slug
        ? `https://installers.energyefficiency.ie/${slug}`
        : ''

    // Use short name for SMS if set, otherwise fall back to full company name
    const displayName = smsCompanyName || companyName || '[Company]'

    // Build SMS previews
    const sms1Preview = `Hi [Name], great news! Based on your info, ${displayName} are the top rated installer in your area (${profileUrl || '[profile URL]'})`

    const ratingText = reviewsRating ? `${reviewsRating}-star` : '[X]-star'
    const platformName = reviewsPlatform || 'Trustpilot'
    const reviewsLinkText = reviewsButtonLink || '[reviews link]'
    const sms2Base = `Hi [Name], ${displayName} has a ${ratingText} ${platformName} rating (${reviewsLinkText}).`
    const sms2Preview = smsPhoneNumber ? `${sms2Base} Call them directly at ${smsPhoneNumber}` : sms2Base

    const SMS_LIMIT = 160

    // Determine which required fields are missing
    function getMissingFields() {
        const missing = []
        if (!slug) missing.push('Slug (set in Settings)')
        if (!reviewsRating) missing.push('Reviews Rating (set in Public Profile → Reviews Configuration)')
        if (!reviewsButtonLink) missing.push('Reviews Link (set in Public Profile → Reviews Configuration)')
        if (!reviewsPlatform) missing.push('Reviews Platform (set in Public Profile → Reviews Configuration)')
        return missing
    }

    const missingFields = getMissingFields()
    const canActivate = missingFields.length === 0

    async function saveDetails() {
        if (smsFollowupsEnabled && !canActivate) {
            NotificationManager.error('Please fill in all required fields before activating SMS followups')
            return
        }

        setLoading(true)
        try {
            const updatedDetails = {
                ...profileDetails,
                smsFollowupsEnabled,
                smsCompanyName,
                smsPhoneNumber
            }
            const updatedCompany = await api.updateProfileDetails(companyDetails.companyId, updatedDetails)
            updateCompany(updatedCompany)
            NotificationManager.success('SMS Followup settings saved')
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{ justifyContent: 'space-between' }}>
                        <Box>
                            <Box component={Typography} variant='h2' marginBottom='0!important'>
                                <Box component='span'>SMS Followups (to customer)</Box>
                            </Box>
                            <Box
                                component='p'
                                fontSize='.875rem'
                                marginBottom='0'
                                marginTop='0'
                                lineHeight='1.7'
                                fontWeight='300'
                            >
                                Configure automated SMS messages sent to leads when assigned to this installer
                            </Box>
                        </Box>
                    </Box>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            />
            <Collapse in={open}>
            <CardContent>
                <Grid container spacing={3}>
                    {/* Active / Inactive Toggle */}
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>SMS Followups Status</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <Select
                                    value={smsFollowupsEnabled ? 'Active' : 'Inactive'}
                                    onChange={(e) => {
                                        const newVal = e.target.value === 'Active'
                                        if (newVal && !canActivate) {
                                            NotificationManager.error('Please fill in all required fields before activating')
                                            return
                                        }
                                        updateField('profileDetails.smsFollowupsEnabled', newVal)
                                    }}
                                    input={<OutlinedInput />}
                                    style={{ marginBottom: '0.5rem' }}
                                >
                                    <MenuItem value='Inactive'>Inactive</MenuItem>
                                    <MenuItem value='Active'>Active</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    {/* SMS Company Name (short name) */}
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>SMS Company Name (optional)</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <OutlinedInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder={companyName || 'e.g. GoKonnect'}
                                    value={smsCompanyName}
                                    onChange={(e) => updateField('profileDetails.smsCompanyName', e.target.value)}
                                />
                            </FormControl>
                            <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                Shortened name for SMS messages. Defaults to "{companyName}" if empty.
                            </Typography>
                        </FormGroup>
                    </Grid>

                    {/* Optional Phone Number */}
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Direct Phone Number (optional)</FormLabel>
                            <FormControl variant='filled' component={Box} width='100%'>
                                <OutlinedInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='e.g. 04779078'
                                    value={smsPhoneNumber}
                                    onChange={(e) => updateField('profileDetails.smsPhoneNumber', e.target.value)}
                                />
                            </FormControl>
                            <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                If provided, SMS #2 will include "Call them directly at [number]"
                            </Typography>
                        </FormGroup>
                    </Grid>

                    {/* Reviews Info (read-only, managed in Public Profile / Reviews Configuration) */}
                    <Grid item xs={12}>
                        <Box
                            p={2}
                            borderRadius={4}
                            style={{ backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}
                        >
                            <Typography variant='body2' style={{ fontWeight: 500, marginBottom: 8 }}>
                                Reviews info (edit in Public Profile card → Reviews Configuration)
                            </Typography>
                            <Typography variant='body2' style={{ color: '#8898aa' }}>
                                <strong>Platform:</strong> {reviewsPlatform || '—'} &nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Rating:</strong> {reviewsRating || '—'} &nbsp;&nbsp;|&nbsp;&nbsp;
                                <strong>Link:</strong> {reviewsButtonLink || '—'}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Missing Fields Warning */}
                    {!canActivate && (
                        <Grid item xs={12}>
                            <Box
                                p={2}
                                borderRadius={4}
                                style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}
                            >
                                <Typography variant='body2' style={{ fontWeight: 500, color: '#856404' }}>
                                    The following fields are required to activate SMS followups:
                                </Typography>
                                <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                                    {missingFields.map((field) => (
                                        <li key={field}>
                                            <Typography variant='body2' style={{ color: '#856404' }}>
                                                {field}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        </Grid>
                    )}

                    {/* Divider */}
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* SMS Previews */}
                    <Grid item xs={12}>
                        <Typography variant='h4' style={{ marginBottom: '1rem' }}>
                            SMS Preview
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            p={2}
                            borderRadius={8}
                            style={{
                                backgroundColor: '#e8f5e9',
                                border: '1px solid #a5d6a7',
                                minHeight: 120
                            }}
                        >
                            <Typography variant='caption' style={{ fontWeight: 600, color: '#2e7d32', display: 'block', marginBottom: 8 }}>
                                SMS #1 — Sent immediately on assignment
                            </Typography>
                            <Typography variant='body2' style={{ color: '#1b5e20', whiteSpace: 'pre-wrap' }}>
                                {sms1Preview}
                            </Typography>
                            <Typography variant='caption' style={{ display: 'block', marginTop: 8, color: sms1Preview.length > SMS_LIMIT ? '#d32f2f' : '#2e7d32' }}>
                                {sms1Preview.length}/{SMS_LIMIT} characters{sms1Preview.length > SMS_LIMIT ? ' ⚠ Over limit — message will be cut off!' : ''}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            p={2}
                            borderRadius={8}
                            style={{
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #90caf9',
                                minHeight: 120
                            }}
                        >
                            <Typography variant='caption' style={{ fontWeight: 600, color: '#1565c0', display: 'block', marginBottom: 8 }}>
                                SMS #2 — Sent next business day at 9am (Mon–Fri)
                            </Typography>
                            <Typography variant='body2' style={{ color: '#0d47a1', whiteSpace: 'pre-wrap' }}>
                                {sms2Preview}
                            </Typography>
                            <Typography variant='caption' style={{ display: 'block', marginTop: 8, color: sms2Preview.length > SMS_LIMIT ? '#d32f2f' : '#1565c0' }}>
                                {sms2Preview.length}/{SMS_LIMIT} characters{sms2Preview.length > SMS_LIMIT ? ' ⚠ Over limit — message will be cut off!' : ''}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Save Button */}
                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='flex-end' width='100%'>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading}
                                onClick={() => saveDetails()}
                            >
                                {!loading ? (
                                    <>Save SMS Settings</>
                                ) : (
                                    <Loader height={12} />
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>
        </Card>
    )
}

export default SmsFollowupsCard
